import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, PlugZap } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import CampaignsManager from "@/components/campaigns/CampaignsManager";

// ---------- Users tab ----------
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);       // full user object being edited, or null
  const [editForm, setEditForm] = useState({ team_id: "", campaign_ids: [], brand_ids: [] });
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({
    email: "", initial_password: "", is_admin: false,
    role: "analyst", team_id: "", brand_ids: [], campaign_ids: [],
  });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/admin/brands").then((r) => setBrands(r.data));
    api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  }, []);

  const create = async () => {
    try {
      const payload = { ...form };
      if (!payload.team_id) delete payload.team_id;
      // Ops users: campaigns are the source of truth; keep brand_ids empty and
      // let the backend derive brand access from the assigned campaigns.
      if (payload.role === "ops") {
        payload.brand_ids = [];
      }
      await api.post("/admin/users", payload);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", initial_password: "", is_admin: false, role: "analyst",
                team_id: "", brand_ids: [], campaign_ids: [] });
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed");
    }
  };

  const update = async (id, patch) => {
    try { await api.patch(`/admin/users/${id}`, patch); load(); toast.success("Updated"); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/admin/users/${id}`);
    load();
  };

  const toggleCampaign = (cid) => {
    setForm((f) => {
      const has = f.campaign_ids.includes(cid);
      return { ...f, campaign_ids: has ? f.campaign_ids.filter((x) => x !== cid) : [...f.campaign_ids, cid] };
    });
  };

  // ---- Edit user (open dialog, toggle campaigns, save) ----
  const openEditUser = (u) => {
    setEditUser(u);
    setEditForm({
      team_id: u.team_id || "",
      campaign_ids: u.campaign_ids || [],
      brand_ids: u.brand_ids || [],
    });
  };
  const toggleEditCampaign = (cid) => {
    setEditForm((f) => {
      const has = f.campaign_ids.includes(cid);
      return { ...f, campaign_ids: has ? f.campaign_ids.filter((x) => x !== cid) : [...f.campaign_ids, cid] };
    });
  };
  const saveEdit = async () => {
    if (!editUser) return;
    try {
      const patch = {
        team_id: editForm.team_id || null,
        campaign_ids: editForm.campaign_ids,
      };
      // Only persist legacy brand_ids for non-ops users
      if (editUser.role !== "ops") {
        patch.brand_ids = editForm.brand_ids;
      }
      await api.patch(`/admin/users/${editUser.id}`, patch);
      toast.success("User updated");
      setEditUser(null);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Save failed");
    }
  };

  // Map campaigns → brand+team labels for display in the list
  const brandByCid = React.useMemo(() => {
    const m = {};
    brands.forEach((b) => { m[b.brand_id] = b; });
    return m;
  }, [brands]);
  const campaignById = React.useMemo(() => {
    const m = {};
    campaigns.forEach((c) => { m[c.id] = c; });
    return m;
  }, [campaigns]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">Roles: Analyst (finance), Ops (calling data — no finance access), Admin (full).</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-tactile" data-testid={ADMIN.newUserBtn}>
              <Plus className="w-4 h-4 mr-1" /> New user
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Email</Label>
                <Input className="glass-input mt-1" data-testid={ADMIN.newUserEmail}
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div><Label>Initial password (min 10 chars)</Label>
                <Input className="glass-input mt-1" data-testid={ADMIN.newUserPassword}
                  value={form.initial_password} onChange={(e) => setForm({ ...form, initial_password: e.target.value })} />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, is_admin: v === "admin" })}>
                  <SelectTrigger className="glass-input mt-1" data-testid="admin-new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="analyst">Analyst (finance)</SelectItem>
                    <SelectItem value="ops">Ops (calling)</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Team</Label>
                <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                  <SelectTrigger className="glass-input mt-1" data-testid="admin-new-user-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.role === "ops" && (
                <div>
                  <Label>Assigned campaigns</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Ops user gets access to whichever brands + teams these campaigns roll up to. Manage the campaign list in <b>Campaigns</b> tab.
                  </p>
                  <div className="glass-solid p-3 mt-1 space-y-1 max-h-56 overflow-y-auto" data-testid="admin-new-user-campaigns">
                    {campaigns.length === 0 && (
                      <p className="text-xs text-slate-500 py-1">No campaigns configured yet — add some in the <b>Campaigns</b> tab first.</p>
                    )}
                    {campaigns.map((c) => {
                      const brand = brandByCid[c.brand_id];
                      const team = teams.find((t) => t.id === c.team_id);
                      return (
                        <label key={c.id} className="text-xs flex items-center gap-2 cursor-pointer py-0.5 hover:bg-white/60 rounded px-1"
                          data-testid={`admin-new-user-camp-${c.id}`}>
                          <input type="checkbox"
                            checked={form.campaign_ids.includes(c.id)}
                            onChange={() => toggleCampaign(c.id)} />
                          <span className="font-medium text-slate-900 flex-1">{c.name}</span>
                          <span className="text-slate-500">{brand?.brand_name || `#${c.brand_id}`}</span>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c.report_type === "FTD" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                            {c.report_type}
                          </span>
                          <span className="text-slate-400 text-[10px]">{team?.name || "—"}</span>
                        </label>
                      );
                    })}
                  </div>
                  {form.campaign_ids.length === 0 && campaigns.length > 0 && (
                    <p className="text-xs text-amber-700 mt-1">Ops user needs at least one campaign assignment.</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid={ADMIN.newUserSubmit}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass-card p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 text-xs uppercase">
              <th className="text-left py-2 px-2">Email</th>
              <th className="text-left py-2 px-2">Role</th>
              <th className="text-left py-2 px-2">Team / Brands</th>
              <th className="text-left py-2 px-2">Active</th>
              <th className="text-left py-2 px-2">2FA</th>
              <th className="text-left py-2 px-2" title="Can this user upload ops files? Admins always can.">Upload</th>
              <th className="text-right py-2 px-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const teamName = teams.find((t) => t.id === u.team_id)?.name;
              const isOps = (u.role === "ops");
              // For ops users show assigned campaigns; for others show legacy brand list
              const campaignLabels = isOps
                ? (u.campaign_ids || [])
                    .map((cid) => campaignById[cid]?.name || "?")
                    .filter(Boolean).join(", ")
                : "";
              const brandLabels = (!isOps || !campaignLabels)
                ? (u.brand_ids || []).map((bid) => brandByCid[bid]?.brand_name || `#${bid}`).join(", ")
                : "";
              return (
                <tr key={u.id} className="border-t border-white/60">
                  <td className="py-2 px-2 font-medium">{u.email}</td>
                  <td className="py-2 px-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">
                      {u.is_super_admin ? "Super admin" : (u.role || (u.is_admin ? "admin" : "analyst"))}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-xs text-slate-600">
                    {teamName || "—"}
                    {campaignLabels && <span className="text-slate-500"> · <span className="font-mono">{campaignLabels}</span></span>}
                    {brandLabels && <span className="text-slate-500"> · {brandLabels}</span>}
                  </td>
                  <td className="py-2 px-2">
                    <Switch checked={u.is_active} onCheckedChange={(v) => update(u.id, { is_active: v })} disabled={u.is_super_admin}
                      data-testid={`${ADMIN.suspendUserBtn}-${u.email}`} />
                  </td>
                  <td className="py-2 px-2">{u.totp_confirmed ? "✓" : "—"}</td>
                  <td className="py-2 px-2">
                    <Switch
                      checked={!!(u.is_admin || u.is_super_admin || u.can_upload_ops)}
                      onCheckedChange={(v) => update(u.id, { can_upload_ops: v })}
                      disabled={u.is_super_admin || u.is_admin}
                      data-testid={`admin-can-upload-${u.email}`}
                    />
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    {!u.is_super_admin && (
                      <button className="text-xs text-[#0A84FF] mr-3" onClick={() => openEditUser(u)}
                        data-testid={`${ADMIN.editUserBtn || "admin-edit-user"}-${u.email}`}>
                        Edit
                      </button>
                    )}
                    <button className="text-xs text-slate-600 mr-3" onClick={() => update(u.id, { reset_2fa: true })} disabled={u.is_super_admin}
                      data-testid={`${ADMIN.reset2faBtn}-${u.email}`}>
                      Reset 2FA
                    </button>
                    <button className="text-xs text-slate-600 mr-3" onClick={() => {
                      const pw = window.prompt("Set new password (min 10 chars):");
                      if (pw) update(u.id, { reset_password: pw });
                    }} disabled={u.is_super_admin}
                      data-testid={`${ADMIN.resetPwdBtn}-${u.email}`}>
                      Reset password
                    </button>
                    {!u.is_super_admin && (
                      <button onClick={() => remove(u.id)} className="text-red-500" data-testid={`${ADMIN.deleteUserBtn}-${u.email}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-edit-user-dialog">
          <DialogHeader><DialogTitle>Edit user{editUser ? ` — ${editUser.email}` : ""}</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div>
                <Label>Team</Label>
                <Select value={editForm.team_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, team_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="glass-input mt-1" data-testid="admin-edit-user-team">
                    <SelectValue placeholder="No team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No team —</SelectItem>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {editUser.role === "ops" ? (
                <div>
                  <Label>Assigned campaigns</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Toggle campaigns on/off. Brand access is derived automatically.
                  </p>
                  <div className="glass-solid p-3 mt-1 space-y-1 max-h-72 overflow-y-auto"
                    data-testid="admin-edit-user-campaigns">
                    {campaigns.length === 0 && (
                      <p className="text-xs text-slate-500 py-1">No campaigns configured yet.</p>
                    )}
                    {campaigns.map((c) => {
                      const brand = brandByCid[c.brand_id];
                      const team = teams.find((t) => t.id === c.team_id);
                      const checked = editForm.campaign_ids.includes(c.id);
                      return (
                        <label key={c.id} className="text-xs flex items-center gap-2 cursor-pointer py-0.5 hover:bg-white/60 rounded px-1"
                          data-testid={`admin-edit-user-camp-${c.id}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleEditCampaign(c.id)} />
                          <span className="font-medium text-slate-900 flex-1">{c.name}</span>
                          <span className="text-slate-500">{brand?.brand_name || `#${c.brand_id}`}</span>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c.report_type === "FTD" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                            {c.report_type}
                          </span>
                          <span className="text-slate-400 text-[10px]">{team?.name || "—"}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {editForm.campaign_ids.length} of {campaigns.length} selected
                    {editForm.campaign_ids.length === 0 && <span className="text-amber-700"> — user won&apos;t have any brand access.</span>}
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Assigned brands (legacy — non-ops role)</Label>
                  <div className="glass-solid p-3 mt-1 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {brands.map((b) => (
                      <label key={b.brand_id} className="text-xs flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.brand_ids.includes(b.brand_id)}
                          onChange={() => {
                            setEditForm((f) => {
                              const has = f.brand_ids.includes(b.brand_id);
                              return { ...f, brand_ids: has ? f.brand_ids.filter((x) => x !== b.brand_id) : [...f.brand_ids, b.brand_id] };
                            });
                          }}
                        />
                        <span>{b.brand_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="admin-edit-user-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ---------- IP whitelist tab ----------
function IpTab() {
  const [ips, setIps] = useState([]);
  const [myIp, setMyIp] = useState("");
  const [form, setForm] = useState({ ip: "", description: "" });

  const load = () => api.get("/admin/ip-whitelist").then((r) => setIps(r.data));
  useEffect(() => {
    load();
    api.get("/whoami_ip").then((r) => setMyIp(r.data.ip));
  }, []);

  const add = async () => {
    try {
      await api.post("/admin/ip-whitelist", form);
      setForm({ ip: "", description: "" });
      load();
      toast.success("IP whitelisted");
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove IP from whitelist?")) return;
    await api.delete(`/admin/ip-whitelist/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 flex items-center justify-between">
        <div>
          <div className="text-sm text-slate-700">Your current IP</div>
          <div className="font-mono text-lg text-slate-900">{myIp || "—"}</div>
        </div>
        <Button variant="outline" onClick={() => setForm({ ...form, ip: myIp })}>
          Use my IP
        </Button>
      </div>

      <div className="glass-card p-4 flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Label>IP address</Label>
          <Input placeholder="203.0.113.42" className="glass-input mt-1" data-testid={ADMIN.newIpInput}
            value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label>Description</Label>
          <Input placeholder="e.g. Steve — office" className="glass-input mt-1" data-testid={ADMIN.newIpDesc}
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <Button onClick={add} className="btn-tactile" data-testid={ADMIN.newIpSubmit}>Add</Button>
      </div>

      <div className="glass-card p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 text-xs uppercase">
              <th className="text-left py-2 px-2">IP</th>
              <th className="text-left py-2 px-2">Description</th>
              <th className="text-left py-2 px-2">Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {ips.map((r) => (
              <tr key={r.id} className="border-t border-white/60">
                <td className="py-2 px-2 font-mono">{r.ip}</td>
                <td className="py-2 px-2">{r.description || "—"}</td>
                <td className="py-2 px-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-2 px-2 text-right">
                  <button onClick={() => remove(r.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- DB connections tab ----------
function DbTab() {
  const [conns, setConns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", mysql_host: "", mysql_port: 3306, mysql_user: "", mysql_password: "", mysql_db: "payment",
    use_jump_host: false, jump_host: "", jump_port: 22, jump_user: "", jump_password: "", jump_private_key: "",
  });
  const [testResult, setTestResult] = useState({});

  const load = () => api.get("/admin/db-connections").then((r) => setConns(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/db-connections", form);
      toast.success("Connection saved");
      setOpen(false);
      load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  const test = async (id) => {
    setTestResult({ ...testResult, [id]: { loading: true } });
    try {
      const r = await api.post(`/admin/db-connections/${id}/test`);
      setTestResult({ ...testResult, [id]: r.data });
      if (r.data.ok) toast.success("Connection OK"); else toast.error(r.data.message);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete connection?")) return;
    await api.delete(`/admin/db-connections/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">MySQL connections + optional SSH jump-host. Credentials encrypted at rest.</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-tactile" data-testid={ADMIN.newDbBtn}><Plus className="w-4 h-4 mr-1" /> New connection</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add MySQL connection</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Connection name</Label><Input className="glass-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>MySQL host</Label><Input className="glass-input mt-1" value={form.mysql_host} onChange={(e) => setForm({ ...form, mysql_host: e.target.value })} /></div>
                <div><Label>Port</Label><Input type="number" className="glass-input mt-1" value={form.mysql_port} onChange={(e) => setForm({ ...form, mysql_port: parseInt(e.target.value || 3306) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>User</Label><Input className="glass-input mt-1" value={form.mysql_user} onChange={(e) => setForm({ ...form, mysql_user: e.target.value })} /></div>
                <div><Label>Password</Label><Input type="password" className="glass-input mt-1" value={form.mysql_password} onChange={(e) => setForm({ ...form, mysql_password: e.target.value })} /></div>
              </div>
              <div><Label>Database name</Label><Input className="glass-input mt-1" value={form.mysql_db} onChange={(e) => setForm({ ...form, mysql_db: e.target.value })} /></div>
              <div className="pt-2 border-t border-slate-200 flex items-center gap-3">
                <Switch checked={form.use_jump_host} onCheckedChange={(v) => setForm({ ...form, use_jump_host: v })} />
                <Label>Use SSH jump host</Label>
              </div>
              {form.use_jump_host && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2"><Label>Jump host</Label><Input className="glass-input mt-1" value={form.jump_host} onChange={(e) => setForm({ ...form, jump_host: e.target.value })} /></div>
                    <div><Label>Port</Label><Input type="number" className="glass-input mt-1" value={form.jump_port} onChange={(e) => setForm({ ...form, jump_port: parseInt(e.target.value || 22) })} /></div>
                  </div>
                  <div><Label>SSH user</Label><Input className="glass-input mt-1" value={form.jump_user} onChange={(e) => setForm({ ...form, jump_user: e.target.value })} /></div>
                  <div><Label>SSH password / key passphrase (leave blank if key has no passphrase)</Label><Input type="password" className="glass-input mt-1" value={form.jump_password} onChange={(e) => setForm({ ...form, jump_password: e.target.value })} /></div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Private key (PEM)</Label>
                      <label className="text-xs text-[#0A84FF] cursor-pointer">
                        Upload .pem
                        <input
                          type="file"
                          className="hidden"
                          accept=".pem,.key,.txt,application/x-pem-file"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const text = await f.text();
                            setForm({ ...form, jump_private_key: text });
                            toast.success(`Loaded ${f.name} (${text.length} chars)`);
                          }}
                        />
                      </label>
                    </div>
                    <Textarea rows={5} className="glass-input mt-1 font-mono text-xs" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;..." value={form.jump_private_key} onChange={(e) => setForm({ ...form, jump_private_key: e.target.value })} />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Tip: EC2 default AMIs only allow key-based auth. Paste your .pem in the password field only if it&apos;s a passphrase — otherwise upload the key here.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid={ADMIN.newDbSubmit}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {conns.map((c) => {
          const t = testResult[c.id];
          return (
            <div key={c.id} className="glass-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-outfit font-semibold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-600 mt-1 font-mono">
                    {c.mysql_user}@{c.mysql_host}:{c.mysql_port}/{c.mysql_db}
                  </div>
                  {c.use_jump_host && (
                    <div className="text-xs text-slate-500 mt-1">via {c.jump_user}@{c.jump_host}:{c.jump_port}</div>
                  )}
                </div>
                <button onClick={() => remove(c.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => test(c.id)} data-testid={ADMIN.testDbBtn}>
                  {t?.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><PlugZap className="w-3 h-3 mr-1" /> Test</>}
                </Button>
                {t && !t.loading && (
                  <span className={`text-xs ${t.ok ? "text-emerald-700" : "text-red-600"}`}>
                    {t.message} {t.elapsed_ms ? `(${t.elapsed_ms} ms)` : ""}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- SQL editor tab ----------
function SqlTab() {
  const [conns, setConns] = useState([]);
  const [connId, setConnId] = useState("");
  const [sql, setSql] = useState("SELECT VERSION(), NOW();");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/db-connections").then((r) => setConns(r.data)); }, []);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/sql/run", { connection_id: connId, sql, limit: 500 });
      setResult(r.data);
    } catch (ex) {
      setResult({ error: ex?.response?.data?.detail || "Failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 space-y-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Connection</Label>
            <Select value={connId} onValueChange={setConnId}>
              <SelectTrigger className="glass-input mt-1" data-testid={ADMIN.sqlConnSelect}>
                <SelectValue placeholder="Choose connection" />
              </SelectTrigger>
              <SelectContent>
                {conns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={run} disabled={!connId || busy} className="btn-tactile" data-testid={ADMIN.sqlRunBtn}
            style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run"}
          </Button>
        </div>
        <div>
          <Label>SQL (read-only: SELECT / SHOW / DESC / EXPLAIN / WITH)</Label>
          <Textarea
            rows={8}
            className="glass-input mt-1 font-mono text-sm bg-white"
            data-testid={ADMIN.sqlEditor}
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            spellCheck={false}
          />
        </div>
      </div>

      <div className="glass-card p-4 overflow-x-auto">
        {!result && <div className="text-sm text-slate-500">No results yet.</div>}
        {result?.error && <div className="text-sm text-red-600 font-mono">{result.error}</div>}
        {result?.columns && (
          <>
            <div className="text-xs text-slate-500 mb-2">{result.row_count} rows in {result.elapsed_ms} ms</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500">
                  {result.columns.map((c) => <th key={c} className="text-left py-2 px-2 whitespace-nowrap">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, i) => (
                  <tr key={i} className="border-t border-white/50">
                    {row.map((v, j) => <td key={j} className="py-1.5 px-2 whitespace-nowrap font-mono">{v === null ? "NULL" : String(v)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Teams tab ----------
function TeamsTab() {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });

  const load = () => api.get("/teams").then((r) => setTeams(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try { await api.post("/teams", form); setForm({ name: "", description: "" }); load(); toast.success("Team created"); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete team AND all its reports?")) return;
    await api.delete(`/teams/${id}`); load();
  };
  const startEdit = (t) => {
    setEditingId(t.id);
    setEditForm({ name: t.name, description: t.description || "" });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/teams/${editingId}`, editForm);
      setEditingId(null); load(); toast.success("Team updated");
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Label>Team name</Label>
          <Input className="glass-input mt-1" data-testid={ADMIN.newTeamName}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label>Description</Label>
          <Input className="glass-input mt-1"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <Button onClick={add} className="btn-tactile" data-testid={ADMIN.newTeamSubmit}>Add</Button>
      </div>
      <div className="glass-card p-4">
        <table className="w-full text-sm">
          <thead><tr className="text-slate-500 text-xs uppercase"><th className="text-left py-2 px-2">Name</th><th className="text-left py-2 px-2">Description</th><th></th></tr></thead>
          <tbody>
            {teams.map((t) => (
              <tr key={t.id} className="border-t border-white/60">
                {editingId === t.id ? (
                  <>
                    <td className="py-2 px-2"><Input className="glass-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></td>
                    <td className="py-2 px-2"><Input className="glass-input" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></td>
                    <td className="py-2 px-2 text-right">
                      <Button size="sm" onClick={saveEdit} className="mr-2">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-2 px-2 font-medium">{t.name}</td>
                    <td className="py-2 px-2">{t.description || "—"}</td>
                    <td className="py-2 px-2 text-right">
                      <button onClick={() => startEdit(t)} className="text-[#0A84FF] text-xs mr-3" data-testid={ADMIN.editTeamBtn}>Edit</button>
                      <button onClick={() => remove(t.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Brands tab ----------
function BrandsTab() {
  const [brands, setBrands] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({ brand_id: "", brand_name: "", currency: "INR" });
  const [editingBid, setEditingBid] = useState(null);
  const [editForm, setEditForm] = useState({ brand_name: "", currency: "INR" });

  const load = () => {
    api.get("/admin/brands").then((r) => setBrands(r.data));
    api.get("/admin/currencies").then((r) => setCurrencies(r.data));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/admin/brands", { ...form, brand_id: parseInt(form.brand_id) });
      setForm({ brand_id: "", brand_name: "", currency: "INR" });
      load(); toast.success("Brand added");
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };
  const startEdit = (b) => { setEditingBid(b.brand_id); setEditForm({ brand_name: b.brand_name, currency: b.currency }); };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/brands/${editingBid}`, editForm);
      setEditingBid(null); load(); toast.success("Brand updated");
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };
  const remove = async (bid) => {
    if (!window.confirm(`Delete brand ${bid}?`)) return;
    await api.delete(`/admin/brands/${bid}`); load();
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 flex gap-3 items-end flex-wrap">
        <div className="w-24">
          <Label>Brand ID</Label>
          <Input className="glass-input mt-1" type="number" data-testid={ADMIN.newBrandId}
            value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label>Brand name</Label>
          <Input className="glass-input mt-1" data-testid={ADMIN.newBrandName}
            value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} />
        </div>
        <div className="w-32">
          <Label>Currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="glass-input mt-1" data-testid={ADMIN.newBrandCurrency}><SelectValue /></SelectTrigger>
            <SelectContent>
              {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={add} className="btn-tactile" data-testid={ADMIN.newBrandSubmit}>Add</Button>
      </div>
      <div className="glass-card p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 text-xs uppercase">
              <th className="text-left py-2 px-2">Brand ID</th>
              <th className="text-left py-2 px-2">Name</th>
              <th className="text-left py-2 px-2">Currency</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {brands.map((b) => (
              <tr key={b.brand_id} className="border-t border-white/60">
                {editingBid === b.brand_id ? (
                  <>
                    <td className="py-2 px-2 font-mono">{b.brand_id}</td>
                    <td className="py-2 px-2"><Input className="glass-input" value={editForm.brand_name} onChange={(e) => setEditForm({ ...editForm, brand_name: e.target.value })} /></td>
                    <td className="py-2 px-2">
                      <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                        <SelectTrigger className="glass-input"><SelectValue /></SelectTrigger>
                        <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Button size="sm" onClick={saveEdit} className="mr-2">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingBid(null)}>Cancel</Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-2 px-2 font-mono">{b.brand_id}</td>
                    <td className="py-2 px-2 font-medium">{b.brand_name}</td>
                    <td className="py-2 px-2"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{b.currency}</span></td>
                    <td className="py-2 px-2 text-right">
                      <button onClick={() => startEdit(b)} className="text-[#0A84FF] text-xs mr-3" data-testid={ADMIN.editBrandBtn}>Edit</button>
                      <button onClick={() => remove(b.brand_id)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Currencies tab ----------
function CurrenciesTab() {
  const [currs, setCurrs] = useState([]);
  const [form, setForm] = useState({ code: "", symbol: "" });
  const [editingCode, setEditingCode] = useState(null);
  const [editSymbol, setEditSymbol] = useState("");

  const load = () => api.get("/admin/currencies").then((r) => setCurrs(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/admin/currencies", form);
      setForm({ code: "", symbol: "" }); load(); toast.success("Currency added");
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/currencies/${editingCode}`, { code: editingCode, symbol: editSymbol });
      setEditingCode(null); load(); toast.success("Updated");
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete ${code}? Brands using it may become inconsistent.`)) return;
    await api.delete(`/admin/currencies/${code}`); load();
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 flex gap-3 items-end flex-wrap">
        <div className="w-32">
          <Label>Code (ISO)</Label>
          <Input className="glass-input mt-1" placeholder="INR" data-testid={ADMIN.newCurrencyCode}
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={4} />
        </div>
        <div className="w-32">
          <Label>Symbol</Label>
          <Input className="glass-input mt-1" placeholder="₹" data-testid={ADMIN.newCurrencySymbol}
            value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} maxLength={4} />
        </div>
        <Button onClick={add} className="btn-tactile" data-testid={ADMIN.newCurrencySubmit}>Add</Button>
      </div>
      <div className="glass-card p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 text-xs uppercase">
              <th className="text-left py-2 px-2">Code</th>
              <th className="text-left py-2 px-2">Symbol</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {currs.map((c) => (
              <tr key={c.code} className="border-t border-white/60">
                <td className="py-2 px-2 font-mono">{c.code}</td>
                <td className="py-2 px-2">
                  {editingCode === c.code
                    ? <Input className="glass-input w-24" value={editSymbol} onChange={(e) => setEditSymbol(e.target.value)} />
                    : (c.symbol || "—")}
                </td>
                <td className="py-2 px-2 text-right">
                  {editingCode === c.code ? (
                    <>
                      <Button size="sm" onClick={saveEdit} className="mr-2">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingCode(null)}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => { setEditingCode(c.code); setEditSymbol(c.symbol || ""); }} className="text-[#0A84FF] text-xs mr-3">Edit</button>
                      <button onClick={() => remove(c.code)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function AdminPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">Admin</h1>
        <p className="text-sm text-slate-600 mt-1">Manage users, IP whitelist, teams, database connections and query MySQL directly.</p>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="glass-panel p-1 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid={ADMIN.tabUsers}>Users</TabsTrigger>
          <TabsTrigger value="teams" data-testid={ADMIN.tabTeams}>Teams</TabsTrigger>
          <TabsTrigger value="brands" data-testid={ADMIN.tabBrands}>Brands</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="admin-tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="currencies" data-testid={ADMIN.tabCurrencies}>Currencies</TabsTrigger>
          <TabsTrigger value="ips" data-testid={ADMIN.tabIps}>IP whitelist</TabsTrigger>
          <TabsTrigger value="db" data-testid={ADMIN.tabDb}>DB connections</TabsTrigger>
          <TabsTrigger value="sql" data-testid={ADMIN.tabSql}>SQL editor</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="teams"><TeamsTab /></TabsContent>
        <TabsContent value="brands"><BrandsTab /></TabsContent>
        <TabsContent value="campaigns"><CampaignsManager /></TabsContent>
        <TabsContent value="currencies"><CurrenciesTab /></TabsContent>
        <TabsContent value="ips"><IpTab /></TabsContent>
        <TabsContent value="db"><DbTab /></TabsContent>
        <TabsContent value="sql"><SqlTab /></TabsContent>
      </Tabs>
    </div>
  );
}
